import { useRef, useState } from 'react';
import { ChevronIcon, FilterLinesIcon } from 'assets/icons';
import { DropDownListItem } from 'common/drop-down-list-item';
import { Menu, PositionType } from 'common/menu';
import { classnames } from 'helpers/utils';
import { useClickOutside } from 'hooks/click-outside';
import styles from './memories-filter.module.scss';

export type MemoriesFilterValue = 'unsorted' | 'all';

interface MemoriesFilterProps {
  value: MemoriesFilterValue;
  counts: Record<MemoriesFilterValue, number>;
  onChange: (value: MemoriesFilterValue) => void;
}

const OPTIONS: { value: MemoriesFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unsorted', label: 'Without Album' },
];

// The dashboard's Memories filter: all memories (the default), or only the
// ones not in any album yet (Figma: GS UI / Design System, node 32474:15277 — Badge).
export const MemoriesFilter = ({
  value,
  counts,
  onChange,
}: MemoriesFilterProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const label = (option: (typeof OPTIONS)[number]) =>
    `${option.label} (${counts[option.value]})`;
  const active = OPTIONS.find((option) => option.value === value) ?? OPTIONS[0];

  return (
    <div className={styles.wrap} ref={ref}>
      <button
        type="button"
        className={classnames(styles.badge, { [styles.badgeOpen]: open })}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <FilterLinesIcon className={styles.icon} />
        <span className={styles.label}>{label(active)}</span>
        <ChevronIcon
          className={classnames(styles.icon, styles.chevron, {
            [styles.chevronOpen]: open,
          })}
        />
      </button>
      {open && (
        <Menu position={PositionType.BottomLeft} className={styles.menu}>
          {OPTIONS.map((option) => (
            <DropDownListItem
              key={option.value}
              className={classnames(styles.item, {
                [styles.itemActive]: option.value === value,
              })}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {label(option)}
            </DropDownListItem>
          ))}
        </Menu>
      )}
    </div>
  );
};
